import React, { FunctionComponent, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useContentLocale } from "../providers/ContentLocaleProvider";
import { useOnClickOutside } from "../hooks/useOnClickOutside";
import { useOnKeyHit } from "../hooks/useOnKeyHit";

export const ContentLocalePicker: FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  const { t } = useTranslation();
  const { locales, contentLocale, setContentLocale } = useContentLocale();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLLIElement | null>(null);

  const close = useCallback(() => setOpen(false), []);
  useOnClickOutside(containerRef, close);
  useOnKeyHit(containerRef, close, "Escape");

  // Nothing to pick between until the Locales load, and no point offering a
  // choice on instances that only support one Locale.
  if (!contentLocale || locales.length < 2) {
    return null;
  }

  return (
    <li
      ref={containerRef}
      id="menubar_content_locale"
      className={`dropdown${open ? " open" : ""}`}
    >
      <a
        className="dropdown-toggle"
        role="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t("language")}
        tabIndex={0}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="glyphicon-pro glyphicon-pro-globe" />{" "}
        <span className="d-none d-sm-inline-block">{contentLocale.name}</span>{" "}
        <span className="caret" />
      </a>
      <ul className="dropdown-menu" role="menu">
        {locales.map((locale) => (
          <li key={locale.id}>
            <a
              role="menuitem"
              tabIndex={0}
              aria-current={locale.id === contentLocale.id}
              onClick={() => setContentLocale(locale)}
            >
              {locale.name}
            </a>
          </li>
        ))}
      </ul>
    </li>
  );
};
